import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { OrderModel } from '@portal-clients/order';
import {
  TransactionV2ListModel,
  transactionV2Client,
} from '@portal-clients/transaction-v2';

import { useClientQuery, useSocketEvent } from '@frontend/hooks';

import { applyOrderEventToTransaction } from './apply-order-event-to-transaction';
import { findTransactionInAccountListsCache } from './find-transaction-in-account-lists-cache';

export interface UseTransactionProps {
  id: string;
  onError?(error: Error): void;
}

export const getTransactionQueryKey = (id: string) => ['transaction', id];

export const useTransactionQuery = ({ id, onError }: UseTransactionProps) => {
  const queryClient = useQueryClient();

  const handleOrderEvent = useCallback(
    (order: OrderModel) => {
      queryClient.setQueryData<TransactionV2ListModel>(
        getTransactionQueryKey(id),
        (transaction) => applyOrderEventToTransaction(transaction, order),
      );
    },
    [id, queryClient],
  );

  useSocketEvent('order', handleOrderEvent);

  return useClientQuery({
    queryKey: getTransactionQueryKey(id),
    async onFetch() {
      return await transactionV2Client.get(id);
    },
    staleTime: 0,
    placeholderData: () => findTransactionInAccountListsCache(queryClient, id),
    onError,
  });
};
